import ReactDOMServer from 'react-dom/server'
import { jsPDF } from 'jspdf'
import "jspdf/dist/polyfills.es.js";
import {CELL_SIZES, SAMPLE_TYPES_CODES} from '../../utils/constants'
import './pdf_table.css'
import {GenerateDate, ToFullSampleTypeName} from "../../utils/helpers";
import {PrepareSamplesForPDF} from "./pdf-helpers";
import {TEST_TYPES} from "../../utils/constants";

const SAMPLES_PER_PAGE = 25

export default function DownloadAsPDF(samples, dairyName, filename = 'download'){
	
	if(samples.length === 0){
		return
	}
	
	let preparedSamples = PrepareSamplesForPDF(samples)
	
	let numberOfPages = Math.ceil(samples.length / SAMPLES_PER_PAGE)
	
	let doc = new jsPDF({
		format: 'letter',
		orientation: 'l',
	})
	
	// based on number of pages
	// create the necessary amount of pages
	for(let i = 0; i < numberOfPages; i++) {
		
		doc.setFontSize(10)
		
		doc.text(
			['Arizona DHIA', 'Microbiology Laboratory', '811 W. Broadway Rd.', 'Tempe, AZ 85282', '(480) 894-0156 ext. 14'],
			139, 8, {
				align: 'center'
			}
		)
		
		doc.setFontSize(13)
		
		doc.text(
			[`Dairy: ${dairyName}`, `Report Date: ${GenerateDate()}`, '', `Key:  + = GROWTH     - = NO GROWTH`],
			10, 10
		)
		
		doc.addPage()
	}
	
	// the number of tables is going to be the same as teh number of pages
	let tableCreateCounter = 0
	
	let html = ''
	
	for(tableCreateCounter; tableCreateCounter < numberOfPages; tableCreateCounter++){
		const indexStart = tableCreateCounter * SAMPLES_PER_PAGE
		const indexEnd = (tableCreateCounter * SAMPLES_PER_PAGE) + SAMPLES_PER_PAGE > samples.length  ? samples.length : (tableCreateCounter * SAMPLES_PER_PAGE) + SAMPLES_PER_PAGE
		
		let samplesForPage = []
		
		for(let j = indexStart; j < indexEnd; j++){
			samplesForPage.push(preparedSamples[j])
		}
		
		html += ReactDOMServer.renderToStaticMarkup(GenerateTable(samples[0].sample_type, samplesForPage))
	}
	
	doc.html(html, {
		x: 9,
		y: 28,
		html2canvas: {
			scale: .28
		},
		callback: (pdf) => {
			pdf.deletePage(numberOfPages + 1)
			
			pdf.save(`${filename}.pdf`)
		}
	})
}

function GenerateTable(sampleType, samples){
	switch(sampleType){
		case SAMPLE_TYPES_CODES.solo:
			return <SoloTablePDF samples={samples}/>
		case SAMPLE_TYPES_CODES.bulk:
			return <BulkTablePDF samples={samples}/>
		case SAMPLE_TYPES_CODES.comp:
			return <ComponentsTablePDF samples={samples}/>
		case SAMPLE_TYPES_CODES.herd:
			return <HerdTablePDF samples={samples}/>
		default:
			console.log('Bad sample code')
	}
}

function SoloTablePDF(props){
	let samples = props.samples
	
	return(
		<table className='pdf-table'>
			<thead>
			<th>Cow ID</th>
			<th>Type</th>
			<th>Sample Date</th>
			<th>NSG</th>
			<th className='contagious-header'>S. aurues</th>
			<th className='contagious-header'>Strep. ag</th>
			<th><i>Staph.</i> other</th>
			<th><i>Strep.</i> other</th>
			<th>Coliform</th>
			<th>Other</th>
			<th>4 Days</th>
			<th>7 Days</th>
			<th>Comments</th>
			</thead>
			<tbody>
			{Object.values(samples).map((sample, idx) => {
				return <tr key={idx}>
					<td className={sample.binary_cow_id.columnSize}>
						{sample.binary_cow_id.value}
					</td>
					<td className={sample.individual_sample_type.columnSize}>
						{sample.individual_sample_type.value}
					</td>
					<td className={sample.sample_date.columnSize}>
						{sample.sample_date.value}
					</td>
					<td className={sample.binary_nsg.columnSize}>
						{sample.binary_nsg.value}
					</td>
					<td className={`${sample.staph_aureus.columnSize} ${sample.staph_aureus.value === '+' ? 'contagious' : ''}`}>
						{sample.staph_aureus.value}
					</td>
					<td className={`${sample.strep_ag.columnSize} ${sample.strep_ag.value === '+' ? 'contagious' : ''}`}>
						{sample.strep_ag.value}
					</td>
					<td className={sample.staph_other.columnSize}>
						{sample.staph_other.value}
					</td>
					<td className={sample.strep_other.columnSize}>
						{sample.strep_other.value}
					</td>
					<td className={sample.coliform.columnSize}>
						{sample.coliform.value}
					</td>
					<td className={sample.other.columnSize}>
						{sample.other.value}
					</td>
					<td className={`${sample.binary_day4.columnSize} ${sample.binary_day4.value === '+' ? 'contagious' : ''}`}>
						{sample.binary_day4.value}
					</td>
					<td className={`${sample.binary_day7.columnSize} ${sample.binary_day7.value === '+' ? 'contagious' : ''}`}>
						{sample.binary_day7.value}
					</td>
					<td className={sample.sample_comments.columnSize} dangerouslySetInnerHTML={{__html: sample.sample_comments.value}}/>
				</tr>
			})}
			</tbody>
		</table>
	)
}

function BulkTablePDF(props){
	
	let samples = props.samples
	
	return(
		<table className='pdf-table bulk-table'>
			<thead>
			<tr>
				<th className="empty-super-header"></th>
				<th className="empty-super-header"></th>
				<th colSpan={2}>Contagious</th>
				<th colSpan={4}>Environmental</th>
				<th id="unitsForSPC">CFU/mL</th>
			</tr>
			<tr>
				<th>Tank</th>
				<th>Sample Date</th>
				<th className='contagious-header'>S. aureus</th>
				<th className='contagious-header'>Strep. ag</th>
				<th><i>Staph.</i> other</th>
				<th><i>Strep.</i> other</th>
				<th>Coliform</th>
				<th>Misc.</th>
				<th headers="unitsForSPC">Total SPC</th>
				<th>LPC</th>
				<th>PI</th>
				<th>Coliform Count</th>
				<th>Salmonella</th>
				<th>Myco Day4</th>
				<th>Myco Day7</th>
				<th>Myco Broth</th>
				<th>Comments</th>
			</tr>
			</thead>
			<tbody>
			{Object.values(samples).map((sample, idx) => {
				return <tr key={idx}>
					<td className={`${sample.bulk_tank.columnSize} small-text`}>
						{sample.bulk_tank.value}
					</td>
					<td className={`${sample.sample_date.columnSize} small-text`}>
						{sample.sample_date.value}
					</td>
					<td className={`${sample.bulk_staph_aureus.columnSize} ${sample.bulk_staph_aureus.value && sample.bulk_staph_aureus.value !== "0" && sample.bulk_staph_aureus.value.toUpperCase() !== "NSG" && sample.bulk_staph_aureus.value !== TEST_TYPES.lost_in_transit && sample.bulk_staph_aureus.value.toUpperCase() !== "N/A" ? 'contagious' : ''}`}>
						{sample.bulk_staph_aureus.value}
					</td>
					<td className={`${sample.bulk_strep_ag.columnSize} ${sample.bulk_strep_ag.value && sample.bulk_strep_ag.value !== "0" && sample.bulk_strep_ag.value.toUpperCase() !== "N/A" && sample.bulk_strep_ag.value.toUpperCase() !== "NSG" ? 'contagious' : ''}`}>
						{sample.bulk_strep_ag.value}
					</td>
					<td className={sample.bulk_staph_other.columnSize}>
						{sample.bulk_staph_other.value}
					</td>
					<td className={sample.bulk_strep_other.columnSize}>
						{sample.bulk_strep_other.value}
					</td>
					<td className={sample.bulk_plate_coliform.columnSize}>
						{sample.bulk_plate_coliform.value}
					</td>
					<td className={sample.bulk_miscellaneous.columnSize}>
						{sample.bulk_miscellaneous.value}
					</td>
					<td className={sample.bulk_spc.columnSize}>
						{sample.bulk_spc.value}
					</td>
					<td className={sample.bulk_lpc.columnSize}>
						{sample.bulk_lpc.value}
					</td>
					<td className={sample.bulk_pi.columnSize}>
						{sample.bulk_pi.value}
					</td>
					<td className={sample.bulk_macconkey_coliform.columnSize}>
						{sample.bulk_macconkey_coliform.value}
					</td>
					<td className={sample.salmonella.columnSize}>
						{sample.salmonella.value}
					</td>
					<td className={`${sample.bulk_myco_day4.columnSize} ${sample.bulk_myco_day4.value === '+' ? 'contagious' : ''}`}>
						{sample.bulk_myco_day4.value}
					</td>
					<td className={`${sample.bulk_myco_day7.columnSize} ${sample.bulk_myco_day7.value === '+' ? 'contagious' : ''}`}>
						{sample.bulk_myco_day7.value}
					</td>
					<td className={`${sample.bulk_myco_broth.columnSize} ${sample.bulk_myco_broth.value === '+' ? 'contagious' : ''}`}>
						{sample.bulk_myco_broth.value}
					</td>
					<td className={sample.sample_comments.columnSize} dangerouslySetInnerHTML={{__html: sample.sample_comments.value}}/>
				</tr>
			})}
			</tbody>
		</table>
	)
}

function ComponentsTablePDF(props) {
	let samples = props.samples
	
	return (
		<table className='pdf-table'>
			<thead>
			<tr>
				<th>Sample Date</th>
				<th>Tank/Pen</th>
				<th>Fat</th>
				<th>True Protein</th>
				<th>Total Protein</th>
				<th>Lactose</th>
				<th>SNF</th>
				<th>SCC</th>
				<th>Comments</th>
			</tr>
			</thead>
			<tbody>
			{Object.values(samples).map((sample, idx) => {
				return <tr key={idx}>
					<td className={`${sample.sample_date.columnSize}`}>
						{sample.sample_date.value}
					</td>
					<td className={`${sample.component_tank_pen.columnSize}`}>
						{sample.component_tank_pen.value}
					</td>
					<td className={`${sample.component_fat.columnSize}`}>
						{sample.component_fat.value}
					</td>
					<td className={`${sample.component_true_protein.columnSize}`}>
						{sample.component_true_protein.value}
					</td>
					<td className={`${sample.component_total_protein.columnSize}`}>
						{sample.component_total_protein.value}
					</td>
					<td className={`${sample.component_lactose.columnSize}`}>
						{sample.component_lactose.value}
					</td>
					<td className={`${sample.component_solids_non_fat.columnSize}`}>
						{sample.component_solids_non_fat.value}
					</td>
					<td className={`${sample.component_somatic_cell_count.columnSize}`}>
						{sample.component_somatic_cell_count.value}
					</td>
					<td className={`${sample.sample_comments.columnSize}`}>
						{sample.sample_comments.value}
					</td>
				</tr>
			})}
			</tbody>
		</table>
	)
}

function HerdTablePDF(props){
	let samples = props.samples
	
	return(
		<table className='pdf-table herd-table'>
			<thead>
			<th>Tube No.</th>
			<th>Pen</th>
			<th>Cow ID</th>
			<th>Sample Date</th>
			<th>NSG</th>
			<th className='contagious-header'>S. aurues</th>
			<th className='contagious-header'>Strep. ag</th>
			<th><i>Staph.</i> other</th>
			<th><i>Strep.</i> other</th>
			<th>Coliform</th>
			<th>Other</th>
			<th>4 Days</th>
			<th>7 Days</th>
			<th>Comments</th>
			</thead>
			<tbody>
			{Object.values(samples).map((sample, idx) => {
				return <tr key={idx}>
					<td className={sample.herd_tube_number.columnSize}>
						{sample.herd_tube_number.value}
					</td>
					<td className={sample.herd_pen.columnSize}>
						{sample.herd_pen.value}
					</td>
					<td className={sample.binary_cow_id.columnSize}>
						{sample.binary_cow_id.value}
					</td>
					<td className={sample.sample_date.columnSize}>
						{sample.sample_date.value}
					</td>
					<td className={sample.binary_nsg.columnSize}>
						{sample.binary_nsg.value}
					</td>
					<td className={`${sample.staph_aureus.columnSize} ${sample.staph_aureus.value === '+' ? 'contagious' : ''}`}>
						{sample.staph_aureus.value}
					</td>
					<td className={`${sample.strep_ag.columnSize} ${sample.strep_ag.value === '+' ? 'contagious' : ''}`}>
						{sample.strep_ag.value}
					</td>
					<td className={sample.staph_other.columnSize}>
						{sample.staph_other.value}
					</td>
					<td className={sample.strep_other.columnSize}>
						{sample.strep_other.value}
					</td>
					<td className={sample.coliform.columnSize}>
						{sample.coliform.value}
					</td>
					<td className={sample.other.columnSize}>
						{sample.other.value}
					</td>
					<td className={`${sample.binary_day4.columnSize} ${sample.binary_day4.value === '+' ? 'contagious' : ''}`}>
						{sample.binary_day4.value}
					</td>
					<td className={`${sample.binary_day7.columnSize} ${sample.binary_day7.value === '+' ? 'contagious' : ''}`}>
						{sample.binary_day7.value}
					</td>
					<td className={sample.sample_comments.columnSize} dangerouslySetInnerHTML={{__html: sample.sample_comments.value}}/>
				</tr>
			})}
			</tbody>
		</table>
	)
}